import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type {
  CreatableTicketCategoryLocationDto,
  CreatableTicketCategoryNodeDto,
  CreatableTicketCategorySearchNodeDto,
} from "@/api/types";
import { Asterisk01Icon } from "@/components/Icons/Asterisk01Icon";
import { Building05Icon } from "@/components/Icons/Building05Icon";
import { Droplets03Icon } from "@/components/Icons/Droplets03Icon";
import { Home02Icon } from "@/components/Icons/Home02Icon";
import { Home04Icon } from "@/components/Icons/Home04Icon";
import { Lightning01Icon } from "@/components/Icons/Lightning01Icon";
import { MarkerPin06Icon } from "@/components/Icons/MarkerPin06Icon";
import { Perspective02Icon } from "@/components/Icons/Perspective02Icon";
import { PlantIcon } from "@/components/Icons/PlantIcon";
import { ThermometerWarmIcon } from "@/components/Icons/ThermometerWarmIcon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { useTranslation } from "@/translations";

import type { CreateTicketModalFormValues } from "../types";
import { CreateTicketModalOptionButton } from "./CreateTicketModalOptionButton";

type SelectTreeLocationStepContentProps = {
  onSelectTicketLocation: (location: CreatableTicketCategoryLocationDto) => void;
};

const SelectTreeLocationStepContent = ({
  onSelectTicketLocation,
}: SelectTreeLocationStepContentProps): React.ReactNode => {
  const { t } = useTranslation();
  const form = useFormContext<CreateTicketModalFormValues>();

  const currCategory = useWatch({
    name: "category",
    control: form.control,
  });

  // Should not happen, but added for type safety
  if (!currCategory?.tree) {
    return null;
  }

  return (
    <ul className="flex h-full flex-col gap-space-sm">
      {/* Safe to assume there is a tree in the selected category at the time of mounting this component  */}
      {currCategory.tree.locations.map((currLocation) => {
        let title = t("page.ticket-category-tree.location-type.inside");
        let description = t("page.ticket-category-tree.location-type.inside.description");
        let icon = <Home04Icon />;
        let dataTestId = "inside-option-btn";
        if (currLocation.location === "outside") {
          title = t("page.ticket-category-tree.location-type.outside");
          description = t("page.ticket-category-tree.location-type.outside.description");
          icon = <MarkerPin06Icon />;
          dataTestId = "outside-option-btn";
        }

        return (
          <li className="w-full" key={currLocation.location}>
            <CreateTicketModalOptionButton
              title={title}
              data-testid={dataTestId}
              description={description}
              icon={icon}
              onSelect={() => onSelectTicketLocation(currLocation)}
            />
          </li>
        );
      })}
    </ul>
  );
};

type SelectTreeRootNodeStepContentProps = {
  onSelectNode: (node: CreatableTicketCategoryNodeDto) => void;
};

const SelectTreeRootNodeStepContent = ({ onSelectNode }: SelectTreeRootNodeStepContentProps): React.ReactNode => {
  const [search, setSearch] = useState("");

  const form = useFormContext<CreateTicketModalFormValues>();
  const currCategory = useWatch({
    name: "category",
    control: form.control,
  });
  const currTree = useWatch({
    name: "tree",
    control: form.control,
  });
  const { t } = useTranslation();

  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());
  const ticketCategoryQueries = useTicketCategoryQueries();
  // Hoist the optional access: the React Compiler (oxc) strips the `?.` when it
  // lifts `currCategory?.id` into a cache-dependency key, producing an unguarded
  // `currCategory.id` that throws while the watched value is undefined.
  const categoryId = currCategory?.id;
  const {
    data: searchResultNodes,
    isFetched: isFetchedSearchResultNodes,
    isFetching: isFetchingSearchResultNodes,
    isError: isErrorSearchResultNodes,
  } = useQuery({
    ...ticketCategoryQueries.categoryCreatableDetails({
      categoryId: categoryId ?? "",
      query: {
        search: debouncedQuery,
      },
    }),
    enabled: currCategory != null && debouncedQuery.length >= 2,
  });

  const getIcon = (node: CreatableTicketCategoryNodeDto) => {
    switch (node.icon) {
      case "structure":
        return <Perspective02Icon />;
      case "waterAndDrainage":
        return <Droplets03Icon />;
      case "climateVentilationAndHeating":
        return <ThermometerWarmIcon />;
      case "electricity":
        return <Lightning01Icon />;
      case "rooms":
        return <Home02Icon />;
      case "buildingExterior":
        return <Building05Icon />;
      case "outdoorSpaces":
        return <PlantIcon />;
      case "communalAreas":
        return <Users01Icon />;
      case "other":
        return <Asterisk01Icon />;
    }
  };

  const [searchResultRootNodes, searchResultLeafNodes] = useMemo(
    () =>
      searchResultNodes?.reduce<[CreatableTicketCategorySearchNodeDto[], CreatableTicketCategorySearchNodeDto[]]>(
        (acc, node) => {
          if (node.type === "root") {
            acc[0].push(node);
          }
          if (node.type === "leaf" || node.type === "leafOther") {
            acc[1].push(node);
          }

          return acc;
        },
        [[], []],
      ) || [[], []],
    [searchResultNodes],
  );
  const rootNodes = currTree?.location?.nodes || [];

  return (
    <section className="flex h-full flex-col gap-space-sm">
      <SearchInput
        placeholder={t("page.resident-tickets.create-modal.form.search-category.placeholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {/* Loading state */}
      {isFetchingSearchResultNodes ? (
        <section className="flex size-full items-center justify-center">
          <FullSizeLoader size="sm" noDelay />
        </section>
      ) : (
        <>
          {/* Default state */}
          {!isFetchedSearchResultNodes && (
            <ul className="flex size-full flex-col gap-space-sm">
              {rootNodes.map((currRootNode) => {
                return (
                  <li className="w-full" key={currRootNode.id}>
                    <CreateTicketModalOptionButton
                      data-testid="root-node-option-btn"
                      title={currRootNode.name}
                      description={currRootNode.description}
                      icon={getIcon(currRootNode)}
                      onSelect={() => onSelectNode(currRootNode)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          {/* Empty / Error state */}
          {(isErrorSearchResultNodes || searchResultNodes?.length === 0) && (
            <section className="flex size-full items-center justify-center">
              <span className="text-body text-text-secondary">{t("common.label.no-result")}</span>
            </section>
          )}
          {/* Success state */}
          {isFetchedSearchResultNodes && searchResultNodes && searchResultNodes.length > 0 && (
            <ul className="flex size-full flex-col gap-space-sm">
              {searchResultRootNodes.map((currRootNode) => {
                return (
                  <li className="w-full" key={currRootNode.id}>
                    <CreateTicketModalOptionButton
                      data-testid="root-node-option-btn"
                      title={currRootNode.name}
                      description={currRootNode.description}
                      icon={getIcon(currRootNode)}
                      onSelect={() => onSelectNode(currRootNode)}
                    />
                  </li>
                );
              })}
              {searchResultLeafNodes.length > 0 && (
                <>
                  {searchResultRootNodes.length > 0 && (
                    <span className="mt-space-lg text-body-emphasis">
                      {t("page.resident-tickets.create-modal.possible-problems")}
                    </span>
                  )}
                  {searchResultLeafNodes.map((currLeafNode) => {
                    return (
                      <li className="w-full" key={currLeafNode.id}>
                        <CreateTicketModalOptionButton
                          title={currLeafNode.name}
                          description={currLeafNode.description}
                          onSelect={() => onSelectNode(currLeafNode)}
                        />
                      </li>
                    );
                  })}
                </>
              )}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

type SelectTreeNodeStepContentProps = {
  onSelectNode: (node: CreatableTicketCategoryNodeDto | CreatableTicketCategorySearchNodeDto) => void;
};

const SelectTreeNodeStepContent = ({ onSelectNode }: SelectTreeNodeStepContentProps): React.ReactNode => {
  const form = useFormContext<CreateTicketModalFormValues>();

  const currTree = useWatch({
    name: "tree",
    control: form.control,
  });

  const activeNode: CreatableTicketCategoryNodeDto | undefined = currTree.path[currTree.path.length - 1];
  const childNodes = activeNode?.children || [];

  return (
    <ul className="flex size-full flex-col gap-space-sm">
      {childNodes.map((currNode) => {
        return (
          <li className="w-full" key={currNode.id}>
            <CreateTicketModalOptionButton
              title={currNode.name}
              description={currNode.description}
              onSelect={() => onSelectNode(currNode)}
            />
          </li>
        );
      })}
    </ul>
  );
};

export const CreateTicketModalDecisionTreeSteps = {
  SelectTreeLocation: SelectTreeLocationStepContent,
  SelectTreeRootNode: SelectTreeRootNodeStepContent,
  SelectTreeNode: SelectTreeNodeStepContent,
};
